import { useState, useEffect, useRef } from "react";

export const useMappingProfiles = (vendor, fetchMappingsList, deleteFieldMappingFn, makeDefaultFn, toastFunctions = {}, connection) => {
  const { showSuccess = () => {}, showError = () => {}, showWarning = () => {} } = toastFunctions;
  const [mappingProfiles, setMappingProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [mappingNames, setMappingNames] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [targetProfileAfterRefresh, setTargetProfileAfterRefresh] = useState(null);

  // Timeout ref to force stop loading after max wait time
  const loadingTimeoutRef = useRef(null);

  // Convert API response to internal format
  const convertApiDataToProfiles = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    const profiles = apiData.map((item) => ({
      id: item.id,
      name: item.name,
      isDefault: item.is_default === 1,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    }));

    // Sort by created_at ascending (oldest first)
    return profiles.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateA - dateB;
    });
  };

  // Fetch mapping profiles from API
  useEffect(() => {
    // Clear any existing timeout when effect runs
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (vendor && fetchMappingsList) {
      setIsLoading(true);

      // Set timeout to force stop loading after 10 seconds
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn("Fetch mappings list timeout - forcing loading state to false");
        setIsLoading(false);
      }, 10000); // 10 seconds timeout

      try {
        fetchMappingsList((response) => {
          // Clear timeout since we got response
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }

          if (response && response.status === "ok" && response.data) {
            let profiles = convertApiDataToProfiles(response.data);

            // If list is empty, create a mock profile
            if (profiles.length === 0) {
              const mockId = `mock-profile-${Date.now()}`;
              const mockProfile = {
                id: mockId,
                name: "Mapping 1",
                isDefault: false,
                mapping_json: [],
                picklist_mapping_json: {},
                isMock: true, // Flag to identify mock profile
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              profiles = [mockProfile];
            }

            setMappingProfiles(profiles);

            // Check if we have a target profile to set after refresh
            if (targetProfileAfterRefresh) {
              // Set to target profile if it exists in the new list
              const targetExists = profiles.find((p) => p.id === targetProfileAfterRefresh);
              if (targetExists) {
                setActiveProfile(targetProfileAfterRefresh);
              } else {
                // Target profile doesn't exist (maybe deleted), fall back to default/first
                const defaultProfile = profiles.find((p) => p.isDefault);
                const initialProfile = defaultProfile || profiles[0];
                if (initialProfile) {
                  setActiveProfile(initialProfile.id);
                }
              }
              setTargetProfileAfterRefresh(null); // Clear target
            } else {
              // Normal flow: Set active profile to default profile if exists, otherwise first profile
              const defaultProfile = profiles.find((p) => p.isDefault);
              const initialProfile = defaultProfile || profiles[0];
              if (initialProfile) {
                setActiveProfile(initialProfile.id);
              }
            }

            // Build mapping names object (without "(Default)" suffix - that will be shown in UI)
            const names = Object.fromEntries(profiles.map((p) => [p.id, p.name]));
            setMappingNames(names);
          }
          setIsLoading(false);
        });
      } catch (error) {
        // Catch any synchronous errors
        console.error("Error calling fetchMappingsList:", error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLoading(false);
      }
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [vendor, fetchMappingsList, refreshTrigger, targetProfileAfterRefresh, connection]);

  const handleAddMappingProfile = () => {
    const newId = `profile-${Date.now()}`;
    const newName = `Mapping ${mappingProfiles.length + 1}`;
    const newProfile = {
      id: newId,
      name: newName,
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setMappingProfiles([...mappingProfiles, newProfile]);
    setMappingNames({ ...mappingNames, [newId]: newName });
    setActiveProfile(newId);
    console.log("Added new mapping profile:", newName);
  };

  const handleDuplicateMappingProfile = (onBeforeSwitch) => {
    const currentProfile = mappingProfiles.find((p) => p.id === activeProfile);
    if (!currentProfile) return;

    const newId = `profile-${Date.now()}`;
    const newName = `${currentProfile.name} (Copy)`;
    const newProfile = {
      id: newId,
      name: newName,
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setMappingProfiles([...mappingProfiles, newProfile]);
    setMappingNames({ ...mappingNames, [newId]: newName });

    // Call callback before switching profile (to prepare duplicated data)
    if (onBeforeSwitch) {
      onBeforeSwitch(newId);
    }

    setActiveProfile(newId);
    console.log("Duplicated mapping profile:", currentProfile.name, "->", newName);
  };

  const handleDeleteMappingProfile = (profileId = null) => {
    // Use provided profileId or fallback to activeProfile
    const targetProfileId = profileId || activeProfile;

    // Validate that targetProfileId is a string
    if (!targetProfileId || typeof targetProfileId !== 'string') {
      console.error("Invalid profileId:", targetProfileId);
      showError("Invalid profile. Please try again.");
      return;
    }

    if (mappingProfiles.length <= 1) {
      showWarning("Cannot delete the last mapping profile");
      return;
    }

    // Check if this is a newly created profile (not saved to server yet)
    const isNewProfile = targetProfileId.startsWith("profile-");

    if (isNewProfile) {
      // Just delete from local state (not saved to server yet)
      const updatedProfiles = mappingProfiles.filter((p) => p.id !== targetProfileId);
      const updatedNames = { ...mappingNames };
      delete updatedNames[targetProfileId];

      setMappingProfiles(updatedProfiles);
      setMappingNames(updatedNames);
      setActiveProfile(updatedProfiles[0].id);
      console.log("Deleted new mapping profile from UI:", targetProfileId);
    } else {
      // Call API to delete from server first
      if (!deleteFieldMappingFn) {
        console.error("deleteFieldMappingFn is not provided");
        return;
      }

      // Confirmation is now handled by the parent component via modal
      deleteFieldMappingFn(targetProfileId, (response) => {
        if (response && response.status === "ok") {
          // Successfully deleted from server, now remove from UI
          const updatedProfiles = mappingProfiles.filter((p) => p.id !== targetProfileId);
          const updatedNames = { ...mappingNames };
          delete updatedNames[targetProfileId];

          setMappingProfiles(updatedProfiles);
          setMappingNames(updatedNames);
          setActiveProfile(updatedProfiles[0].id);
          console.log("Deleted mapping profile:", targetProfileId);
          showSuccess("Mapping profile deleted successfully!");
        } else {
          const errorMessage = response?.message || "Failed to delete mapping profile. Please try again.";
          showError(errorMessage);
        }
      });
    }
  };

  const handleRenameMappingProfile = (e) => {
    const newName = e.target.value;
    // Only update mappingNames (for Save All to use)
    // Don't update mappingProfiles.name until Save All succeeds
    setMappingNames({ ...mappingNames, [activeProfile]: newName });
    console.log("Updated mapping name (not saved yet):", newName);
  };

  // Update profile ID after successful creation (converts temporary ID to real UUID from API)
  const updateProfileIdAfterCreation = (oldId, newId) => {
    // Update mappingProfiles array
    const updatedProfiles = mappingProfiles.map((p) =>
      p.id === oldId ? { ...p, id: newId } : p
    );
    setMappingProfiles(updatedProfiles);

    // Update mappingNames - move the name from old ID to new ID
    const updatedNames = { ...mappingNames };
    updatedNames[newId] = updatedNames[oldId];
    delete updatedNames[oldId];
    setMappingNames(updatedNames);

    // Update active profile to the new ID
    setActiveProfile(newId);

    console.log(`Updated profile ID from ${oldId} to ${newId}`);
  };

  // Update profile name after successful save (commits the edited name)
  const commitProfileName = (profileId) => {
    const newName = mappingNames[profileId];
    if (!newName) return;

    const updatedProfiles = mappingProfiles.map((p) =>
      p.id === profileId ? { ...p, name: newName } : p
    );
    setMappingProfiles(updatedProfiles);
    console.log(`Committed profile name to: ${newName}`);
  };

  // Make profile default
  const handleMakeDefault = (onSuccess, onError, profileId = null) => {
    // Use provided profileId or fallback to activeProfile
    const targetProfileId = profileId || activeProfile;

    // Validate that targetProfileId is a string
    if (!targetProfileId || typeof targetProfileId !== 'string') {
      console.error("Invalid profileId:", targetProfileId);
      showError("Invalid profile. Please try again.");
      if (onError) onError();
      return;
    }

    // Check if this is a new profile (not saved to server yet)
    const isNewProfile = targetProfileId.startsWith("profile-");

    if (isNewProfile) {
      showWarning("Please save this profile first before making it default.");
      if (onError) onError();
      return;
    }

    if (!makeDefaultFn) {
      console.error("makeDefaultFn is not provided");
      if (onError) onError();
      return;
    }

    makeDefaultFn(targetProfileId, (response) => {
      if (response && response.status === "ok") {
        // Trigger refresh to reload the list with updated default status
        setRefreshTrigger((prev) => prev + 1);
        // Call onSuccess callback
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMessage = response?.message || "Failed to set profile as default. Please try again.";
        showError(errorMessage);
        if (onError) onError();
      }
    });
  };

  // Force refresh profiles list
  const refreshProfiles = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Force refresh profiles list while keeping the specified profile active
  const refreshProfilesAndKeepActive = (profileId) => {
    setTargetProfileAfterRefresh(profileId);
    setRefreshTrigger((prev) => prev + 1);
  };

  // Wrapper for setActiveProfile that removes unsaved profiles when selecting saved profile
  const handleSetActiveProfile = (profileId) => {
    // Check if the selected profile is a saved profile (has UUID, not temporary ID)
    const isNewProfile = profileId.startsWith("profile-");

    // If switching to a saved profile, remove unsaved profiles from local list
    if (!isNewProfile) {
      // Filter out temporary profiles (those with "profile-" prefix)
      const savedProfiles = mappingProfiles.filter((p) => !p.id.startsWith("profile-"));

      // Only update if there were temporary profiles to remove
      if (savedProfiles.length !== mappingProfiles.length) {
        setMappingProfiles(savedProfiles);

        // Clean up mappingNames for removed profiles
        const updatedNames = {};
        savedProfiles.forEach((p) => {
          if (mappingNames[p.id]) {
            updatedNames[p.id] = mappingNames[p.id];
          }
        });
        setMappingNames(updatedNames);

        console.log("Removed unsaved profiles from list");
      }
    }

    // Set active profile (works for both saved and unsaved profiles)
    setActiveProfile(profileId);

    // Note: Loading state will be handled by useFieldMappings hook when it fetches data
  };

  return {
    mappingProfiles,
    setMappingProfiles,
    activeProfile,
    setActiveProfile: handleSetActiveProfile,
    mappingNames,
    setMappingNames,
    handleAddMappingProfile,
    handleDuplicateMappingProfile,
    handleDeleteMappingProfile,
    handleRenameMappingProfile,
    handleMakeDefault,
    updateProfileIdAfterCreation,
    commitProfileName,
    refreshProfiles,
    refreshProfilesAndKeepActive,
    isLoading,
  };
};
